import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { listAgents, AgentSummary } from '../api/agents';
import { getCalls } from '../api/calls';
import api from '../api/client';
import { setAgents as setReduxAgents } from '../store/slices/agentsSlice';
import { setCallHistory } from '../store/slices/callsSlice';
import { UsageWidget } from './dashboard/UsageWidget';
import { OnboardingWizard } from './onboarding/OnboardingWizard';
import { OnboardingChecklist } from './onboarding/OnboardingChecklist';
import { getOnboardingStatus } from '../api/onboarding';
import type { OnboardingStatus } from '../api/onboarding';

interface PhoneNumber {
  phone_number_id: string;
  twilio_phone_number: string;
  is_active: boolean;
}

interface PhonesResponse {
  phones: PhoneNumber[];
  pagination?: { total: number };
}

function StatusBadge({ status }: { status: string }) {
  const s = status?.toLowerCase();
  if (s === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399' }}>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
        Active
      </span>
    );
  }
  if (s === 'paused') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24' }}>
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 inline-block" />
        Paused
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(107,114,128,0.2)', color: '#9ca3af' }}>
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block" />
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Draft'}
    </span>
  );
}

interface CallRecord {
  call_id: string;
  caller_number?: string;
  status: string;
  duration?: number;
  agent_id?: string;
  started_at: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [phones, setPhones] = useState<PhoneNumber[]>([]);
  const [recentCalls, setRecentCalls] = useState<CallRecord[]>([]);
  const [callsThisMonth, setCallsThisMonth] = useState<number>(0);
  const [minutesUsed, setMinutesUsed] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  // Onboarding state
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

        const [agRes, phRes, callsRes, onboardingRes] = await Promise.all([
          listAgents(1, 20),
          api.get<PhonesResponse>('/api/v1/phones').catch(() => ({ data: { phones: [] } })),
          getCalls({ limit: 10, date_from: monthStart }).catch(() => ({ calls: [], pagination: { total: 0 } })),
          getOnboardingStatus().catch(() => null),
        ]);
        const agentsList = agRes.agents || [];
        const phonesList = (phRes.data as PhonesResponse).phones || [];
        const calls = callsRes?.calls || [];
        const totalCalls = callsRes?.pagination?.total || calls.length;

        setAgents(agentsList);
        setPhones(phonesList);
        setRecentCalls(calls.slice(0, 5));
        setCallsThisMonth(totalCalls);

        const totalSeconds = calls.reduce((sum: number, c: CallRecord) => sum + (c.duration || 0), 0);
        setMinutesUsed(Math.round(totalSeconds / 60));

        // Sync to Redux
        dispatch(setReduxAgents(agentsList as any));
        dispatch(setCallHistory(calls as any));

        // Handle onboarding
        if (onboardingRes) {
          setOnboardingStatus(onboardingRes);
          // Auto-show wizard for first-time users who haven't completed/skipped
          if (!onboardingRes.completed && !onboardingRes.skipped) {
            setShowWizard(true);
          }
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [dispatch]);

  const activeAgents = agents.filter((a) => a.status === 'active').length;
  const recentAgents = agents.slice(0, 3);

  const handleWizardClose = () => {
    setShowWizard(false);
    // Refresh onboarding status
    getOnboardingStatus().then(setOnboardingStatus).catch(() => {});
  };

  const handleWizardComplete = () => {
    setShowWizard(false);
    getOnboardingStatus().then(setOnboardingStatus).catch(() => {});
  };

  const handleChecklistContinue = () => {
    setShowWizard(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      {/* Onboarding Wizard (full-screen modal) */}
      {showWizard && onboardingStatus && (
        <OnboardingWizard
          initialStatus={onboardingStatus}
          onClose={handleWizardClose}
          onComplete={handleWizardComplete}
        />
      )}

      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400 mt-1">Welcome to CallPilot — your AI call operations center.</p>
        </div>
        {/* Sidebar checklist trigger (inline version — full sidebar integration below) */}
        {onboardingStatus && !onboardingStatus.completed && !onboardingStatus.skipped && (
          <button
            onClick={() => setShowWizard(true)}
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-opacity hover:opacity-80"
            style={{ background: 'rgba(139,92,246,0.2)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.3)' }}
          >
            🚀 Setup guide
          </button>
        )}
      </div>

      {/* SECTION 2: Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Agents', value: agents.length, icon: '🤖', color: '#6366f1' },
          { label: 'Active Agents', value: activeAgents, icon: '✅', color: '#10b981' },
          { label: 'Phone Numbers', value: phones.length, icon: '📞', color: '#3b82f6' },
          { label: 'Calls This Month', value: callsThisMonth, icon: '📋', color: '#f59e0b' },
        ].map((stat) => (
          <div
            key={stat.label}
            className="rounded-xl p-5"
            style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-400">{stat.label}</span>
              <span className="text-lg">{stat.icon}</span>
            </div>
            <div className="text-2xl font-bold text-white">{stat.value}</div>
          </div>
        ))}
      </div>

      {/* SECTION 3: Recent Agents */}
      {recentAgents.length > 0 && (
        <div
          className="rounded-xl p-4 md:p-6 mb-6 md:mb-8"
          style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-white">Recent Agents</h2>
            <Link
              to="/agents"
              className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              View all agents →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recentAgents.map((agent) => (
              <div
                key={agent.agent_id}
                className="rounded-lg p-4 cursor-pointer transition-all duration-150"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                onClick={() => navigate(`/agents/${agent.agent_id}/edit`)}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-semibold text-white truncate flex-1 mr-2">{agent.agent_name}</h3>
                  <StatusBadge status={agent.status} />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Created {new Date(agent.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SECTION 4: Recent Calls */}
      {recentCalls.length > 0 && (
        <div
          className="rounded-xl p-4 md:p-6 mb-6 md:mb-8"
          style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-white">Recent Calls</h2>
            <Link to="/call-logs" className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors">
              View all logs →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 text-xs uppercase">
                  <th className="pb-3 pr-4">Caller</th>
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3 pr-4">Duration</th>
                  <th className="pb-3">Time</th>
                </tr>
              </thead>
              <tbody>
                {recentCalls.map((call) => (
                  <tr key={call.call_id} className="border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                    <td className="py-3 pr-4 text-gray-300">{call.caller_number || 'Unknown'}</td>
                    <td className="py-3 pr-4">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          background: call.status === 'completed' ? 'rgba(16,185,129,0.15)' : call.status === 'failed' ? 'rgba(248,113,113,0.15)' : 'rgba(99,102,241,0.15)',
                          color: call.status === 'completed' ? '#34d399' : call.status === 'failed' ? '#f87171' : '#818cf8',
                        }}
                      >
                        {call.status?.charAt(0).toUpperCase() + call.status?.slice(1)}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-gray-400">
                      {call.duration ? `${Math.floor(call.duration / 60)}:${String(call.duration % 60).padStart(2, '0')}` : '—'}
                    </td>
                    <td className="py-3 text-gray-400">{new Date(call.started_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {minutesUsed > 0 && (
            <p className="text-xs text-gray-500 mt-3">
              {minutesUsed} minutes used this month
            </p>
          )}
        </div>
      )}

      {/* Usage Widget */}
      <UsageWidget />

      {/* Onboarding Checklist (persistent sidebar card) */}
      {onboardingStatus && !onboardingStatus.completed && !onboardingStatus.skipped && (
        <div className="mb-6">
          <OnboardingChecklist
            steps={onboardingStatus.steps}
            currentStep={onboardingStatus.currentStep}
            onContinue={handleChecklistContinue}
          />
        </div>
      )}

      {/* SECTION 5: Quick actions */}
      <div
        className="rounded-xl p-4 md:p-6"
        style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <h2 className="text-lg font-semibold text-white mb-5">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate('/agents')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all duration-150"
            style={{ background: '#6366f1', border: '1px solid rgba(99,102,241,0.5)' }}
          >
            + Create Agent
          </button>
          <button
            onClick={() => navigate('/agents')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#d1d5db' }}
          >
            📁 Upload KB
          </button>
          <button
            onClick={() => navigate('/phones')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#d1d5db' }}
          >
            📞 Get a Number
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
